import { LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { provide } from "@lit/context";
import {
  envContext,
  authContext,
  AuthState,
  renderSpecContext,
} from "@/context";
import { EnvConfig, RenderSpec } from "@/types";
import { initTheme } from "@/utils";
import { initSdk } from "@/funcs/env";
import { Auth } from "@saasbase-io/loginflow-websdk";
import {
  API_DOMAIN,
  API_HOST,
  API_PATH,
  DEFAULT_LOCALE,
  DEFAULT_PROJECT_ID,
} from "@/constants";

@customElement("sb-provider")
export class SbProvider extends LitElement {
  createRenderRoot() {
    return this;
  }

  /**
   * ENV now comes from a property instead of getEnvConfig()
   */
  @property({ type: Object })
  @provide({ context: envContext })
  env: EnvConfig | null = null;

  /**
   * Auth state
   */
  @state()
  @provide({ context: authContext })
  auth: AuthState = { isAuthenticated: false };

  @state()
  @provide({ context: renderSpecContext })
  renderSpec: string | null = null;

  /**
   * Enable/disable automatic redirect for authenticated users on auth pages.
   */
  @property({ type: Boolean })
  autoRedirect: boolean = true;

  private getEffectiveEnv(): EnvConfig | null {
    if (!this.env) return null;
    // Compute apiBaseUrl from locale, or use default locale
    const locale = this.env.locale || DEFAULT_LOCALE;
    const apiBaseUrl = `https://${API_HOST}.${locale}.${API_DOMAIN}/${API_PATH}`;
    return {
      ...this.env,
      apiBaseUrl: this.env.apiBaseUrl ?? apiBaseUrl,
      projectId: this.env.projectId ?? DEFAULT_PROJECT_ID,
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    initTheme();

    const waitForEnv = async () => {
      while (!this.env || !this.env.domain) {
        await new Promise((res) => setTimeout(res, 20));
      }

      const effectiveEnv = this.getEffectiveEnv();
      if (!effectiveEnv) return;

      Auth.configure({
        domain: effectiveEnv.domain,
        appId: effectiveEnv.appId,
        clientSecret: effectiveEnv.clientSecret ?? "",
        locale: effectiveEnv.locale,
        signinUrl: effectiveEnv.signinUrl,
        signupUrl: effectiveEnv.signupUrl,
        onFlowStateChange: (event) => {
          if (event.render_spec) {
            this.renderSpec = event.render_spec;
          }
        },
      });

      initSdk(effectiveEnv);
      this.refreshAuthState();
    };

    waitForEnv().catch((err) =>
      console.error("Error initializing SbProvider:", err),
    );
  }

  updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);

    if (changedProperties.has("env")) {
      // Reconfigure SDK if env changes
      if (this.env?.domain) {
        const effectiveEnv = this.getEffectiveEnv();
        if (!effectiveEnv) return;

        Auth.configure({
          domain: effectiveEnv.domain,
          appId: effectiveEnv.appId,
          clientSecret: effectiveEnv.clientSecret ?? "",
          locale: effectiveEnv.locale,
          signinUrl: effectiveEnv.signinUrl,
          signupUrl: effectiveEnv.signupUrl,
          onFlowStateChange: (event) => {
            if (event.render_spec) {
              this.renderSpec = event.render_spec;
            }
          },
        });

        initSdk(effectiveEnv);
      }
    }

    if (changedProperties.has("auth")) {
      this.handleAuthStateChange();
    }
  }

  private handleAuthStateChange(): void {
    if (!this.autoRedirect || !this.auth.isAuthenticated) return;
    if (!this.env) return;

    const currentPath = window.location.pathname;
    const authPageUrls: string[] = [this.env.signinUrl, this.env.signupUrl];
    const isOnAuthPage = authPageUrls.some((authUrl) =>
      currentPath.includes(authUrl),
    );

    if (isOnAuthPage) {
      const qs = new URLSearchParams(window.location.search);
      const redirect_url = qs.get("redirect_url");
      const signinRedirectUrl: string =
        redirect_url || this.env.signinRedirectUrl || "/";
      window.location.href = signinRedirectUrl;
    }
  }

  private refreshAuthState() {
    this.auth = { isAuthenticated: Auth.isAuthenticated() };
  }
}
